/**
 * Shared API/request layer.
 *
 * Goal: stop hand-writing the same axios block in every action creator.
 * Historically each call repeated:
 *   axios({
 *     method, url: `${API_URL}/...`,
 *     headers: {
 *       Accept: 'application/json',
 *       'Content-Type': 'application/json',
 *       'x-access-token': `${getCookie('user_token')}`,
 *     },
 *     data,
 *   })
 *
 * Use `apiClient` (a preconfigured axios instance) instead, either with a config
 * object — `apiClient({ method: 'get', url: `/surveys/${id}` })`, the form the action
 * creators use — or through the thin `get/post/put/del` wrappers. `baseURL` is
 * `API_URL`, so pass paths WITHOUT the API_URL prefix (e.g. `/surveys/preferences/${formId}`).
 * The auth headers are injected automatically by the request interceptor; per-request
 * headers still override them, so only pass the header that differs
 * (e.g. `headers: { Accept: 'application/zip' }`).
 *
 * Notes:
 * - This module intentionally does NOT centralize the `statusCode === 498` /
 *   logout handling: today that lives in each caller and reads a body field
 *   (`response.data.statusCode`), so moving it would change behavior. Callers keep
 *   their existing success/498/error branching unchanged.
 * - `apiClient` sends the user token and is for token-authenticated API_URL requests
 *   only. Calls using Basic auth (login/signup) and unauthenticated API_URL endpoints
 *   go through `publicClient`, which has the same baseURL but injects no headers.
 * - SSO requests use `utils/ssoApi.js`; survey-api requests use `utils/surveyApi.js`.
 *   Metrics and third-party hosts keep using the raw `axios` import.
 */
import axios from 'axios';
import { notification } from 'antd';
import { API_URL } from './constants';
import { getCookie } from './helpers';
import axiosTiming from './axios-timing';

/** Standard token-auth headers, exported for code still using the raw axios import. */
export const authHeaders = (extra = {}) => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'x-access-token': `${getCookie('user_token')}`,
  ...extra,
});

const apiClient = axios.create({ baseURL: API_URL });

// Inject auth headers on every request; per-request headers take precedence.
apiClient.interceptors.request.use(config => {
  config.headers = { ...authHeaders(), ...(config.headers || {}) };
  return config;
});

// Preserve the existing request-timing / custMetric behavior on this instance.
axiosTiming(apiClient);

/** Same baseURL, no injected headers: for API_URL endpoints that must not send the token. */
const publicClient = axios.create({ baseURL: API_URL });
axiosTiming(publicClient);

/** Thin verb helpers so callers stop building config objects by hand. */
export const get = (url, config) => apiClient.get(url, config);
export const post = (url, data, config) => apiClient.post(url, data, config);
export const put = (url, data, config) => apiClient.put(url, data, config);
export const del = (url, config) => apiClient.delete(url, config);

/**
 * Standardizes the repeated `notification.error({ message: intl.formatMessage(...) })`
 * pattern. `descriptor` is a react-intl message descriptor from utils/commonwords.
 */
export const notifyError = (intl, descriptor) =>
  notification.error({ message: intl.formatMessage(descriptor) });

/**
 * Looks up geo/locale info for the visitor. With `viaIpify`, the client IP is
 * resolved through ipify first and sent along; otherwise the API uses the request IP.
 * Resolves with the `/ipinfo` response body.
 */
export const fetchIpInfo = ({ viaIpify = false } = {}) => {
  const lookup = data =>
    publicClient({ method: 'post', url: '/ipinfo', data }).then(info => info.data);
  if (!viaIpify) return lookup();
  return axios({ method: 'get', url: 'https://api.ipify.org?format=json' }).then(
    response => lookup({ ip: response.data.ip })
  );
};

/**
 * Renders `html` to a PDF on the API and saves it as `saveAs`. Resolves without
 * downloading when the render request does not return statusCode 200.
 */
export const downloadPdf = ({ html, fileName, saveAs }) =>
  apiClient({
    method: 'post',
    url: `/utils/pdfdownload`,
    headers: { Accept: 'application/pdf' },
    data: { html, fileName },
  }).then(response => {
    if (response.data.statusCode !== 200) return undefined;
    return publicClient({
      method: 'get',
      responseType: 'blob',
      url: `/utils/pdfdownload?fileName=${response.data.fileName}`,
    }).then(file => {
      const fileDownload = require('react-file-download');
      window.open(fileDownload(new Blob([file.data]), saveAs), '_blank');
    });
  });

export { apiClient, publicClient };
export default apiClient;
